import ProductItemLayout, {
  ProductItemLayoutProps
} from "@/pages/settings/components/settingsItems/ProductItemLayout";
import SettingItemLayout, {
  SettingItemLayoutProps
} from "@/pages/settings/components/settingsItems/SettingItemLayout";
import ProductCard from "@/pages/settings/pages/products-v2/components/ProductCard";
import Skeleton from "@/components/ui/skeleton";
import Text from "@/components/ui/text";
import React, { ReactNode } from "react";

export interface SectionProps {
  title?: string;
  subTitle?: string;
  subTitleBreakAfterSentence?: boolean;
  action?: ReactNode;
  items?: SettingItemLayoutProps[] | ProductItemLayoutProps[];
  isProduct?: boolean;
  isProductV2?: boolean;
  children?: ReactNode;
  isLoading?: boolean;
}

// The rounded, bordered card that wraps a section's item rows. Exported so consumers that
// render custom section children (instead of the `items` prop) can reuse the exact same
// container styling rather than duplicating the class string.
export const SETTINGS_CARD_CONTAINER_CLASS =
  "flex flex-col rounded-2xl overflow-hidden border border-border-border1 bg-surface-elevation1";

export const processSubtitleWithBreak = (text: string): string => {
  // If text already contains manual line breaks, don't apply automatic sentence breaking
  if (text.includes("\n")) {
    return text;
  }
  const sentenceEndRegex = /(\.\s+)(?=[A-Z])/;
  return text.replace(sentenceEndRegex, ".\n");
};

const Section: React.FC<SectionProps> = ({
  title,
  subTitle,
  subTitleBreakAfterSentence = false,
  action,
  items = [],
  isProduct = false,
  isProductV2 = false,
  children,
  isLoading
}) => {
  return (
    <div className="w-full flex flex-col gap-4">
      {title && (
        <div className="flex w-full flex-col justify-start gap-2">
          <div className="flex flex-row justify-between items-center">
            {isLoading ? (
              <Skeleton className="h-6 w-[5.5rem]" />
            ) : (
              <Text variant="lgLabel">{title}</Text>
            )}
            {isLoading ? <Skeleton className="h-6 w-[3rem]" /> : action}
          </div>
          {subTitle ? (
            isLoading ? (
              <Skeleton className="h-6 w-full" />
            ) : (
              <Text
                variant="bodySecondary"
                className={
                  subTitleBreakAfterSentence ? "whitespace-pre-line" : undefined
                }
              >
                {subTitleBreakAfterSentence
                  ? processSubtitleWithBreak(subTitle)
                  : subTitle}
              </Text>
            )
          ) : null}
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-[15.5rem] w-full" />
      ) : (
        items.length > 0 && (
          <>
            {isProductV2 ? (
              <div className="flex flex-col gap-6">
                {items.map((item: any, index) => {
                  return (
                    <ProductCard
                      key={item.productId}
                      product={item.product}
                      index={index}
                      onEdit={item.onEditProduct}
                      onAddServiceItem={item.onAddServiceItem}
                      onEditServiceItem={item.onEditServiceItem}
                      onDeleteServiceItem={item.onDeleteServiceItem}
                      onDragEndServiceItems={item.onDragEndServiceItems}
                    />
                  );
                })}
              </div>
            ) : (
              <div className={SETTINGS_CARD_CONTAINER_CLASS}>
                {items.map((item, index) => (
                  <div key={index}>
                    {isProduct ? (
                      <ProductItemLayout
                        {...(item as ProductItemLayoutProps)}
                        key={index}
                      />
                    ) : (
                      <div key={index}>
                        <SettingItemLayout
                          {...(item as SettingItemLayoutProps)}
                          isLastItem={index === items.length - 1}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )
      )}
      {isLoading ? <Skeleton className="h-[4.75rem] w-full" /> : children}
    </div>
  );
};

export default Section;
